import { useState, useEffect, useMemo, useRef } from "react";
import {
  buildCdnURL,
  formatPrice,
  getDiscount,
  calculatePricePerUnit,
  formatWeightUnitShort,
} from "@/utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addToCart,
  removeFromCart,
  updateCartQuantity,
} from "@/store/slices/cartSlice";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const htmlToText = (value) => {
  if (!value) return "";
  const input = String(value);

  // Fast-path: no tags.
  if (!/<[^>]+>/.test(input)) {
    return input.replace(/\s+/g, " ").trim();
  }

  // Browser-safe parsing (handles entities too).
  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(input, "text/html");
      const text = (doc?.body?.textContent || "").replace(/\s+/g, " ").trim();
      return text;
    } catch {
      // fall through
    }
  }

  // Fallback: strip tags.
  return input
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const ProductCard = ({ data, loading }) => {
  const { t } = useTranslation();
  const product = useMemo(() => {
    return {
      _id: data?._id,
      price: data?.stockEntries?.[0]?.price,
      sellingPrice: data?.stockEntries?.[0]?.sellingPrice,
      name: data?.name,
      description: data?.description,
      images: data?.images,
      variant: data?.variants?.[0],
      stockId: data?.stockEntries?.[0]?._id,
      availableQuantity: data?.stockEntries?.[0]?.availableQuantity,
      pausedOnApp: data?.stockEntries?.[0]?.pausedOnApp,
      pauseAfter: data?.stockEntries?.[0]?.pauseAfter,
    };
  }, [data]);

  const isOutOfStock = !product?.availableQuantity;

  // Check if product is paused
  const isProductPaused = () => {
    if (!product?.pausedOnApp) return false;

    // If pausedOnApp is true but no pauseAfter field, product is paused
    if (!product?.pauseAfter) return true;

    // If pauseAfter exists, check if current time is past the pause time
    const pauseTime = new Date(product.pauseAfter);
    const currentTime = new Date();
    return currentTime > pauseTime;
  };

  const productPaused = isProductPaused();

  const displayDescription = useMemo(() => {
    const text = htmlToText(product?.description);
    return text;
  }, [product?.description]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartProducts = useSelector((state) => state?.cart?.cartProducts);
  const [quantity, setQuantity] = useState(1);
  const [isShowingQuantityOption, setIsShowingQuantityOption] = useState(false);

  const stockId = product?.stockId;
  const productId = product?._id;

  const cartProduct = cartProducts?.find(
    (product) => product?.stockId === stockId
  );
  const variant = product?.variant;
  const isCartQuantityMoreThanAvailable =
    cartProduct?.quantity > product?.availableQuantity;
  const quantityInputRef = useRef(null);

  useEffect(() => {
    if (cartProduct) {
      setQuantity(cartProduct?.quantity);
      setIsShowingQuantityOption(true);
    }
  }, [product, cartProduct]);

  useEffect(() => {
    if (quantityInputRef.current) {
      quantityInputRef.current.value = quantity;
    }
  }, [quantity, isShowingQuantityOption]);

  if (loading) {
    return <div className="w-full h-[200px] skeleton section"></div>;
  }

  const discount = getDiscount(product?.price, product?.sellingPrice);
  const savingsAmount =
    product?.price && product?.sellingPrice
      ? product.price - product.sellingPrice
      : 0;

  const handleProductClick = () => {
    if (isProductPaused()) return toast.error(t("productDetails.productNotAvailable"));

    // Pass state to indicate navigation from product list
    const navigationState = { fromProductList: true };

    // For out of stock products, pass productId as query param to use alternative API
    if (isOutOfStock) {
      navigate(`/product/${stockId}?productId=${productId}`, { state: navigationState });
    } else {
      navigate(`/product/${stockId}`, { state: navigationState });
    }
  };

  const handleAddToCart = () => {
    if (isOutOfStock) return;

    if (productPaused) {
      toast.error(t("productDetails.productNotAvailable"));
      return;
    }

    if (!cartProduct) {
      // Create cart item with fallback approach - use the unified product data
      const cartItem = {
        _id: product._id,
        price: product.price,
        sellingPrice: product.sellingPrice,
        images: product.images,
        variant: product.variant,
        name: product.name,
        stockId: product.stockId,
        quantity: quantity || 1,
        availableQuantity: product.availableQuantity,
      };

      dispatch(addToCart(cartItem));
      setIsShowingQuantityOption(true);
    }
  };

  const handleQuantityChange = (newQ) => {
    let newQuantity = newQ;
    if (newQuantity >= 0 || newQuantity === "") {
      toast.dismiss();
      if (newQuantity > product?.availableQuantity) {
        toast.error(
          `Only ${product?.availableQuantity} items available in stock.`
        );
        newQuantity = product?.availableQuantity || 0;
      }
      setQuantity(+newQuantity);
      if (!newQuantity && cartProduct) {
        setIsShowingQuantityOption(false);
        return dispatch(removeFromCart(stockId));
      }
      if (cartProduct) {
        dispatch(updateCartQuantity({ stockId, quantity: newQuantity || 0 }));
      }
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-background-200 dark:border-border-200 border rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md h-full flex flex-col">
        <div className="relative">
          <img
            src={
              buildCdnURL(product?.images?.[0]) || "/assets/placeholder.webp"
            }
            alt={product?.name}
            className={`w-full h-40 object-contain p-2 ${
              productPaused ? "opacity-50" : ""
            }`}
            onClick={handleProductClick}
          />

          {/* Savings badge at top-left */}
          {product?.price !== product?.sellingPrice &&
            savingsAmount > 0 &&
            !productPaused && (
              <div className="absolute top-1.5 left-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white px-1.5 py-0.5 rounded shadow-md">
                <div className="flex flex-col items-start leading-none">
                  <span className="text-[8px] font-medium">Save</span>
                  <span className="text-[10px] font-bold">
                    ₹{formatPrice(savingsAmount)}
                  </span>
                </div>
              </div>
            )}

          {/* Status indicators and controls in image section */}
          <div className="absolute bottom-2 right-2">
            {isShowingQuantityOption && !productPaused ? (
              <div
                className={`flex items-center bg-white dark:bg-white border border-orange-300 rounded-md px-1 py-1 shadow-sm h-7 ${
                  isCartQuantityMoreThanAvailable && "border-red-500"
                }`}
              >
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  className="w-5 h-5 flex items-center justify-center text-orange-600 hover:bg-orange-100 rounded text-sm"
                >
                  <span className="font-bold">−</span>
                </button>
                <input
                  ref={quantityInputRef}
                  type="number"
                  onBlur={(e) => {
                    const newQuantity = e.target.value;
                    handleQuantityChange(+newQuantity || 0);
                  }}
                  className="quantity w-6 text-center text-xs font-semibold border-none focus:outline-none !bg-transparent dark:!bg-transparent text-gray-900 dark:text-gray-900 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  min="0"
                />
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  className="w-5 h-5 flex items-center justify-center text-orange-600 hover:bg-orange-100 rounded text-sm"
                >
                  <span className="font-bold">+</span>
                </button>
              </div>
            ) : isOutOfStock ? (
              <div className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded shadow-sm h-7 flex items-center">
                Out of Stock
              </div>
            ) : productPaused ? (
              <div className="bg-gray-500 text-white text-xs font-medium px-2 py-1 rounded shadow-sm h-7 flex items-center">
                Not Available
              </div>
            ) : (
              !cartProduct && (
                <button
                  onClick={handleAddToCart}
                  className="group w-7 h-7 bg-white border border-orange-300 text-orange-600 rounded-md flex items-center justify-center hover:bg-orange-50 shadow-sm transition-all duration-300 ease-in-out"
                >
                  <span className="text-sm font-bold">+</span>
                </button>
              )
            )}
          </div>
        </div>

        <div className="p-2 flex flex-col flex-grow gap-2">
          <div className="flex gap-2 items-center">
            {/* Variant/size info */}
            <div
              className={`text-xs bg-gray-100 px-1 rounded-sm font-bold ${
                productPaused ? "text-gray-400 dark:text-gray-700" : "text-gray-500 dark:text-gray-800"
              }`}
            >
              {variant?.title && (
                <span>
                  {variant.title}
                </span>
              )}
            </div>

            {/* Price per unit */}
            {variant?.weight &&
              variant?.weightUnit &&
              product?.sellingPrice && (
                <div className="text-xs text-gray-500">
                  ({" "}
                  {calculatePricePerUnit(
                    product.sellingPrice,
                    variant.weight,
                    variant.weightUnit
                  )}{" "}
                  )
                </div>
              )}
          </div>

          {/* Product title */}
          <h3
            className={`text-xs font-bold line-clamp-2 leading-tight ${
              productPaused ? "text-gray-400" : "text-gray-800"
            }`}
          >
            {product?.name}
          </h3>

          {/* Product description - always show with 2 lines */}
          {displayDescription && (
            <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-tight">
              {displayDescription}
            </div>
          )}

          {/* Price section with discount */}
          {product?.sellingPrice && (
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-solid border-yellow-300 w-max rounded-md overflow-hidden">
                <span
                  className={`text-sm font-bold bg-yellow-300 px-1.5 ${
                    productPaused ? "text-gray-400" : "text-black"
                  }`}
                >
                  ₹{formatPrice(product?.sellingPrice)}
                </span>
                {product?.price !== product?.sellingPrice && product?.price && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 line-through px-1.5">
                    ₹{formatPrice(product?.price)}
                  </span>
                )}
              </div>
              {/* Discount percentage */}
              {product?.price !== product?.sellingPrice && discount && (
                <span className="text-green-500 text-xs font-bold">
                  {discount}% OFF
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
